import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Absolute ceiling on the loading state — no matter how weird the
// OAuth callback flow gets, ProtectedRoute must never spin forever.
// If nothing has flipped `loading` to false within this window, we
// treat the exchange as failed, drop to unauthenticated, and let
// ProtectedRoute bounce to /login where the user can retry.
const LOADING_STATE_HARD_CEILING_MS = 25_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadingWatchdogRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me", { timeout: 10_000 });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, KEEP loading=true.
    // AuthCallback is mid-flight exchanging the session_id; ProtectedRoute
    // must show the loading spinner instead of bouncing to /login on the
    // brief window between hash-detection and setUser() committing.
    // AuthCallback explicitly calls setLoading(false) once user is set.
    if (window.location.hash?.includes("session_id=")) {
      // Hard-ceiling safety net: if AuthCallback fails to unstick the
      // loading state within LOADING_STATE_HARD_CEILING_MS, we forcibly
      // check auth ourselves so no user ever gets a permanently spinning
      // "LOADING SESSION…" screen in production.
      loadingWatchdogRef.current = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.warn("[AuthContext] loading watchdog fired — forcing checkAuth");
        checkAuth();
      }, LOADING_STATE_HARD_CEILING_MS);
      return () => {
        if (loadingWatchdogRef.current) {
          clearTimeout(loadingWatchdogRef.current);
          loadingWatchdogRef.current = null;
        }
      };
    }
    checkAuth();
  }, [checkAuth]);

  // Clear the watchdog once loading flips false (AuthCallback finished
  // successfully or errored gracefully). Prevents a late-firing watchdog
  // from double-checking auth and flickering the UI.
  useEffect(() => {
    if (!loading && loadingWatchdogRef.current) {
      clearTimeout(loadingWatchdogRef.current);
      loadingWatchdogRef.current = null;
    }
  }, [loading]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
